import { apiClient, API_PATHS } from '@/services/api';

export async function fetchPartnerNames() {

    try {
        const response = await apiClient.rfp.get(API_PATHS.rfp.partnerConfigs.list);
        
        // Ensure data is an array before processing
        if (!Array.isArray(response.data)) {
            console.error('Unexpected response format:', response.data);
            return [];
        }

        // Map, filter out empty partner names, and sort alphabetically
        const mappedPartners = response.data
            .map(partner => ({
                partnerName: partner.partnerName?.trim() || partner.name?.trim() || '',
                identifierInDataWarehouse: partner.identifierInDataWarehouse,
                apiIntegrationVersion: partner.apiIntegrationVersion,
                useRydpayApis: partner.useRydpayApis || false,
                useFleetApis: partner.useFleetApis || false,
                sponsors: Array.isArray(partner.sponsors) ? partner.sponsors : [],
                id: partner.id
            }))
            .filter(partner => partner.partnerName !== '') // Remove empty or whitespace-only names
            .sort((a, b) => a.partnerName.localeCompare(b.partnerName, 'en', { sensitivity: 'base' })); // Sort alphabetically
        
        // DEBUG: Log mapped result
        console.log('Mapped partners:', mappedPartners);
        
        return mappedPartners;

    } catch (error) {
        console.error('Error fetching partner names:', error);
        throw error; // Rethrow error to handle it in the component
    }
}

/**
 * Paginated partner search against the rp-partner-configs collection (not sponsors).
 * Rows are mapped to the shape PartnerAutocompleteModal renders:
 * name / portalUserGroup = partnerName (portal userGroup), legalName = companyLegalName.
 * @param {{ searchTerm?: string, page?: number, pageSize?: number }} options
 * @returns {Promise<{ items: Array, total: number, totalPages: number }>}
 */
export async function fetchPartnerConfigAutocomplete(options = {}) {
    const { searchTerm = '', page = 1, pageSize = 20 } = options;
    const offset = (page - 1) * pageSize;

    try {
        const response = await apiClient.rfp.get(API_PATHS.rfp.partnerConfigs.list, {
            params: {
                paginated: 1,
                limit: pageSize,
                offset,
                ...(searchTerm ? { search: searchTerm } : {}),
            },
        });
        const items = (response.data?.items || []).map(partner => ({
            _id: partner.id,
            name: partner.partnerName,
            legalName: partner.companyLegalName || '',
            portalUserGroup: partner.partnerName,
            featurePackTag: (partner.sponsors || [])
                .map(sponsor => sponsor.featurePackTag)
                .filter(Boolean)
                .join(', '),
        }));
        const total = response.data?.pagination?.total ?? items.length;
        return { items, total, totalPages: Math.ceil(total / pageSize) };
    } catch (error) {
        console.error('Error fetching partner config autocomplete:', error);
        return { items: [], total: 0, totalPages: 0 };
    }
}